import { useCallback, useEffect, useState } from 'react';
import { Euro, Plus, Trash2 } from 'lucide-react';
import { Button, Card, IconButton, formatDatum, formatEuro } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';
import {
  revenueEntriesApi,
  type RevenueEntry,
} from '@/lib/api';

interface RevenueEntriesWidgetProps {
  /** Called after create/delete so parent can refetch dashboard (GoalTracker). */
  onChange?: () => void;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export function RevenueEntriesWidget({ onChange }: RevenueEntriesWidgetProps) {
  const [entries, setEntries] = useState<RevenueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await revenueEntriesApi.list();
      setEntries(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Umsatz-Einträge ließen sich nicht laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const amountNumber = Number.parseFloat(amount);
  const canSubmit =
    !submitting &&
    name.trim().length > 0 &&
    Number.isFinite(amountNumber) &&
    amountNumber > 0 &&
    date.length === 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      await revenueEntriesApi.create({
        name: name.trim(),
        amount: amountNumber,
        date,
      });
      setName('');
      setAmount('');
      setDate(todayIso());
      await load();
      onChange?.();
    } catch (err) {
      console.error(err);
      setError('Eintrag ließ sich nicht speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await revenueEntriesApi.delete(id);
      await load();
      onChange?.();
    } catch (err) {
      console.error(err);
      setError('Eintrag ließ sich nicht löschen');
    }
  };

  const total = entries.reduce((sum, e) => sum + Number.parseFloat(e.amount), 0);

  return (
    <Card
      icon={Euro}
      title="Umsatz-Einträge"
      actions={
        <span className="t-small text-muted-foreground">
          Summe: <span className="t-body-strong tabular text-foreground">{formatEuro(total)}</span>
        </span>
      }
    >
      <div className="space-y-4">
        {/* Inline form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_140px_160px_auto]">
          <TextField
            label="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            placeholder="Projekt Klinikum"
          />
          <TextField
            label="Betrag (EUR)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="0.01"
            min="0.01"
            required
            placeholder="5000"
          />
          <TextField label="Datum" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          <Button type="submit" icon={Plus} disabled={!canSubmit} loading={submitting}>
            Eintragen
          </Button>
        </form>

        {error && <p className="t-small text-danger">{error}</p>}

        {/* List */}
        <div className="border-t border-border pt-4">
          {loading ? (
            <p className="t-small text-muted-foreground">Lädt</p>
          ) : entries.length === 0 ? (
            <p className="t-small text-muted-foreground">Umsatz-Einträge erscheinen hier, sobald einer erfasst ist.</p>
          ) : (
            <ul className="divide-y divide-border">
              {entries.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between py-2">
                  <div className="flex min-w-0 items-center gap-4">
                    <span className="w-24 shrink-0 t-mono text-muted-foreground">{formatDatum(entry.date)}</span>
                    <span className="truncate">{entry.name}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="t-mono text-foreground">{formatEuro(entry.amount)}</span>
                    <IconButton icon={Trash2} label="Eintrag löschen" variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
